/**
 * First-visit location popup.
 * Reuses the theme's native <modal-dialog> element (see global.js) for
 * open/close, focus trapping and Escape handling, and Shopify's native
 * localization form for the actual country / language switch. This file
 * only decides *when* to show the popup and remembers that a visitor has
 * already seen it.
 *
 * Editor note: <modal-dialog> relocates itself to be a direct child of
 * <body> as soon as it connects (see global.js ModalDialog). When the
 * theme editor live-reloads this section after a settings change, the OLD
 * instance is left behind under <body> (now outside the reloaded section)
 * while a brand new one is inserted and immediately relocated too — so we
 * end up with two. On every 'shopify:section:load' we drop all but the
 * newest one and (re)bind it, otherwise settings changes look like they
 * do nothing because you're still looking at the stale copy.
 *
 * Editor note: in the theme editor we only show the popup while THIS
 * section is the one selected in the sidebar (shopify:section:select /
 * shopify:section:deselect), instead of on every load. Otherwise it pops
 * back open — covering the whole canvas — every time a merchant edits any
 * OTHER section, which makes the editor unusable without exiting preview
 * first. `editorSelected` lives outside bind() so it survives this
 * section reloading while selected (e.g. while tweaking its own
 * settings), and clears automatically once the merchant selects something
 * else.
 *
 * The country field is a single combined text+datalist input (see
 * setupCountryCombobox) rather than a plain <select> once JS runs: click
 * it and the browser shows the full country list like a dropdown, or type
 * to narrow it down — one field doing both jobs. The underlying <select>
 * is kept in the DOM (hidden, not removed) as the actual source of truth
 * Shopify's form handler reads, so a visitor with JS disabled still gets
 * a fully working plain dropdown.
 */
(function () {
  var COOKIE_NAME = 'location_popup_seen';
  var SELECTOR = 'modal-dialog[data-location-popup]';
  var editorSelected = false;

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  function removeStaleModals(keep) {
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      if (el !== keep) el.remove();
    });
  }

  // Shopify appends "shpxid" to the URL when it redirects a visitor across
  // domains as part of a Markets localization change (e.g. after they pick
  // a country that lives on a different domain). Landing with that param
  // means the visitor just made their choice seconds ago on another one of
  // our domains — asking again here would just be repeating the same
  // question. This is an undocumented Shopify implementation detail, so
  // treat it as a best-effort heuristic: worst case it doesn't match and
  // the popup simply shows once more, nothing breaks.
  function arrivedViaMarketRedirect() {
    return /(?:^|[?&])shpxid=/.test(window.location.search);
  }

  /**
   * Pre-select the country the visitor is *probably* in, rather than the
   * country this domain happens to serve.
   *
   * Without this, a visitor from the US landing on lofotenseaweed.no gets
   * a popup already showing "Norge" and is asked to confirm the wrong
   * answer — and if they do, every price and the cart shipping estimate
   * they see are Norway's, not theirs. Market-pinned domains (.no, .eu)
   * always serve their own market regardless of where the visitor is, so
   * the served country is a poor default on exactly the domains where
   * getting it wrong costs the most.
   *
   * browsing_context_suggestions.json is Shopify's own localization
   * discovery endpoint: same-origin, no API key, no third party, and — the
   * point — backed by the same GeoIP data Shopify itself uses to resolve
   * markets, so its answer can never contradict what checkout will do. A
   * DIY IP lookup is not an option anyway: Liquid exposes no visitor IP
   * (request only carries design_mode, host, locale, origin, page_type,
   * visual_preview_mode), so it would have to be a client-side call to a
   * third-party geo API — public key in the theme source, per-request
   * cost, visitor IPs sent to a vendor, and an answer that can disagree
   * with Shopify's.
   *
   * country[enabled]=true restricts the result to countries we actually
   * sell to. We deliberately do NOT pass country[exclude] (which the docs
   * example uses): that suppresses the suggestion when detection matches
   * the served country, and we want the detected value either way.
   *
   * This only changes the dropdown's *default*. The visitor still has to
   * press a button below — no auto-apply, no auto-redirect. Shopify's own
   * guidance is explicit that auto-redirecting on detection is the wrong
   * pattern.
   *
   * Failure is silent by design: any error, timeout or unexpected shape
   * just leaves the served country selected, exactly as before.
   */
  function prefillDetectedCountry(modal) {
    var select = modal.querySelector('select[name="country_code"]');
    if (!select || !window.Shopify || !window.Shopify.routes || !window.Shopify.routes.root) return;

    // The country served by this domain, captured before the request goes
    // out, so we can tell afterwards whether the visitor has touched the
    // dropdown in the meantime and avoid clobbering their choice.
    var servedCode = select.value;

    // routes.root is required: it carries the /de/ or /no/ locale prefix
    // on our subfolder web presences, where a bare /browsing_context...
    // path would resolve against the wrong locale.
    var url = window.Shopify.routes.root + 'browsing_context_suggestions.json?country[enabled]=true';

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var detected =
          data &&
          data.detected_values &&
          data.detected_values.country &&
          data.detected_values.country.handle;
        if (!detected || detected === servedCode) return;

        // Visitor already picked something while the request was in
        // flight — leave their choice alone.
        if (select.value !== servedCode) return;

        // Only apply a country we actually offer. country[enabled]=true
        // should guarantee this, but the <select> is the authority.
        var option = select.querySelector('option[value="' + detected + '"]');
        if (!option) return;

        select.value = detected;
        // setupCountryCombobox (below) listens for this and keeps the
        // visible text field in sync — this function doesn't need to know
        // whether that combobox exists at all.
        select.dispatchEvent(new Event('change', { bubbles: true }));
      })
      .catch(function () {
        /* leave the served country selected */
      });
  }

  /**
   * Replace the plain country <select> with a single combined field: one
   * visible text input, backed by a native <datalist>, that can either be
   * clicked open (the browser shows the full list of countries, same as a
   * dropdown) or typed into to narrow that list down live. Feedback on an
   * earlier version of this was that a separate search box stacked above
   * the dropdown read as two fields for one job — this is one field doing
   * both.
   *
   * The <select name="country_code"> itself is kept in the DOM and is
   * still exactly what Shopify's own form handler reads on submit — it's
   * only hidden from view, never removed or disabled, so with JS off (or
   * failing) the visitor still gets a fully working plain dropdown, just
   * without the combined-field treatment. Every change the visible input
   * makes is written straight onto the select, with a real 'change' event
   * dispatched, and a listener on the select's own 'change' event keeps
   * the visible input's text in sync with whatever the select ends up
   * holding (including from prefillDetectedCountry, above) — so the two
   * only ever need to agree in one direction each way, never coordinate
   * directly.
   *
   * Skipped entirely for short lists (see the length check below) — a
   * combined search field above four or five options is friction, not
   * help.
   */
  function setupCountryCombobox(modal, form) {
    var select = form.querySelector('select[name="country_code"]');
    var selectWrap = select && select.closest('.location-popup-modal__select-wrap');
    var field = selectWrap && selectWrap.closest('.location-popup-modal__field');
    if (!select || !selectWrap || !field) return;

    var options = Array.prototype.map.call(select.querySelectorAll('option'), function (option) {
      return { value: option.value, label: option.textContent.trim() };
    });
    if (options.length < 8) return;

    function labelFor(value) {
      var match = options.filter(function (opt) {
        return opt.value === value;
      })[0];
      return match ? match.label : '';
    }

    function valueForLabel(label) {
      var needle = label.trim().toLowerCase();
      var match = options.filter(function (opt) {
        return opt.label.toLowerCase() === needle;
      })[0];
      return match ? match.value : null;
    }

    var listId = 'PopupCountryList-' + (modal.dataset.sectionId || 'popup');

    var datalist = document.createElement('datalist');
    datalist.id = listId;
    options.forEach(function (opt) {
      var dOption = document.createElement('option');
      dOption.value = opt.label;
      datalist.appendChild(dOption);
    });

    var visibleLabel = field.querySelector('label');

    var input = document.createElement('input');
    input.type = 'text';
    // Reuses the select's own class so it inherits the exact same box,
    // border, padding and caret-clearance styling — visually this IS the
    // field the select used to be, not a second one next to it.
    input.className = select.className;
    input.setAttribute('list', listId);
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('spellcheck', 'false');
    if (visibleLabel && visibleLabel.id) input.setAttribute('aria-labelledby', visibleLabel.id);
    input.placeholder = modal.dataset.filterPlaceholder || 'Type or choose a country';
    input.value = labelFor(select.value);

    // Clicking or tabbing into the field clears it, rather than leaving
    // the current selection ("Norway") sitting there pre-filled. Feedback
    // was that starting from an existing suggestion made it feel like you
    // had to notice, then select-all-and-delete it before you could type
    // your own answer — the opposite of the "start typing to narrow it
    // down" experience this field is meant to offer. Clearing on focus
    // also means the browser's native datalist popup shows the FULL
    // country list immediately (matching against an empty string), the
    // same as clicking open a plain <select>, instead of pre-filtering to
    // matches for whatever was already selected.
    //
    // Nothing is lost if the visitor focuses and then leaves without
    // picking anything: the blur handler below always resets the visible
    // text back to whatever the (unchanged) select still holds.
    input.addEventListener('focus', function () {
      input.value = '';
    });

    input.addEventListener('input', function () {
      var matched = valueForLabel(input.value);
      // Only ever push a value onto the select once the typed text is an
      // exact match for a real country — never a half-typed guess — so
      // the form always holds the visitor's last genuine choice.
      if (matched && matched !== select.value) {
        select.value = matched;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // If the visitor leaves the field on something that isn't a real
    // country (a typo, text left over from narrowing the list down
    // without finishing, or simply focused and clicked away without
    // typing anything after the focus handler above cleared it), snap the
    // visible text back to whatever is actually selected rather than
    // leaving it blank or holding stray, unsubmittable text.
    input.addEventListener('blur', function () {
      input.value = labelFor(select.value);
    });

    // Enter confirms/narrows, it never submits the form early — the
    // visitor still presses one of the buttons below either way, same as
    // picking straight from a plain dropdown would have been.
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') event.preventDefault();
    });

    // Keeps the visible text correct no matter what changes the select's
    // value — this listener, not the input's own code, is what makes
    // prefillDetectedCountry (above) work without knowing this combobox
    // exists at all.
    select.addEventListener('change', function () {
      input.value = labelFor(select.value);
    });

    select.style.display = 'none';
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;
    selectWrap.insertBefore(input, select);
    selectWrap.appendChild(datalist);
  }

  /**
   * window.Shopify.customerPrivacy is attached by Shopify's own privacy
   * script, which is a separate script from this one and can still be
   * loading when this file runs — even though this file is loaded with
   * `defer`, nothing guarantees Shopify's privacy script has already run
   * by then. Checking it exactly once, synchronously, can therefore
   * conclude "no consent needed" simply because the API hadn't attached
   * itself yet, on a visit that genuinely does need to ask — which is
   * exactly the failure mode that let a real fresh (private-window)
   * visitor skip the consent question entirely.
   *
   * This polls briefly instead of checking once, so a slightly slower
   * page still gets the right branch, and gives up after `timeoutMs` so a
   * visitor is never stuck forever with neither button group showing.
   * There's no documented Shopify event for "the API just attached
   * itself" to listen for instead, so polling (cheap, and only for a
   * moment right at page load) is the reliable option here.
   */
  function waitForCustomerPrivacy(callback, timeoutMs) {
    var deadline = Date.now() + (timeoutMs || 2000);
    (function poll() {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.shouldShowBanner === 'function') {
        callback(cp);
        return;
      }
      if (Date.now() >= deadline) {
        callback(null);
        return;
      }
      window.setTimeout(poll, 50);
    })();
  }

  /**
   * Wire up the location + cookie-consent actions.
   *
   * Shopify's own consent model defines a "preferences" category as
   * "storing preferences, such as country or language, to personalize
   * their experience" — literally what this popup does. So this isn't
   * bundling two unrelated choices behind one set of buttons: it's asking
   * the one question ("what do you want us to remember about you?") that
   * the location choice was already secretly a part of.
   *
   * Two variants share this one form. If cookie consent is still
   * undecided for this visitor (Shopify would otherwise be showing them
   * its own banner), both are offered together:
   *   - "allow all cookies"   -> setTrackingConsent(all true)
   *   - "essential only"      -> setTrackingConsent(all false)
   * Both submit the SAME form with whatever country/language is selected
   * — declining cookies never costs the visitor the localization feature
   * itself, which is what keeps this from being a consent dark pattern.
   * If consent was already decided on an earlier visit, neither of those
   * buttons is relevant and the plain "Update" button is shown instead.
   *
   * Both button groups start (and stay) hidden per the markup until
   * waitForCustomerPrivacy resolves, rather than defaulting to the plain
   * "Update" button while waiting: showing that button early and having
   * the visitor click it before we hear back would submit the form with
   * no consent question ever asked, on a visit that may have needed one —
   * a brief moment with no buttons visible is the safer failure mode than
   * that.
   *
   * A "Manage cookie settings" link is always available regardless of
   * variant, and reopens Shopify's own granular preferences modal via
   * window.privacyBanner.showPreferences() rather than us building a
   * second, competing settings UI — same reasoning as leaning on
   * browsing_context_suggestions.json above instead of a DIY geo lookup.
   * Our own modal is deliberately left open underneath it (see the
   * comment on that click handler below) rather than hidden.
   */
  function setupLocationAndConsentActions(modal, form) {
    var consentGroup = form.querySelector('[data-location-popup-consent-actions]');
    var simpleButton = form.querySelector('[data-location-popup-simple-action]');
    var manageLink = form.querySelector('[data-location-popup-manage-cookies]');

    function submitForm() {
      // requestSubmit() (unlike the older form.submit()) dispatches a real
      // 'submit' event, so the existing "remember the popup was answered"
      // listener below still fires. Fall back for older browsers.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }

    waitForCustomerPrivacy(function (cp) {
      // Any failure here (API never showed up, method throws) is treated
      // the same as "nothing to combine" — show the plain location-only
      // button rather than guess at a consent UI that might not match
      // what Shopify would otherwise be enforcing.
      var needsConsent = false;
      try {
        needsConsent = !!(cp && cp.shouldShowBanner());
      } catch (e) {
        needsConsent = false;
      }

      if (needsConsent && consentGroup) {
        consentGroup.hidden = false;
        if (simpleButton) simpleButton.hidden = true;
      } else if (simpleButton) {
        simpleButton.hidden = false;
        if (consentGroup) consentGroup.hidden = true;
      }
    });

    // Click handlers are attached immediately rather than waiting on
    // waitForCustomerPrivacy above — the buttons stay hidden until that
    // resolves anyway, so there's nothing to click prematurely, and this
    // way the handlers are guaranteed ready the instant either group
    // becomes visible.
    if (consentGroup) {
      consentGroup.querySelectorAll('[data-location-popup-consent]').forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();

          var granted = button.getAttribute('data-location-popup-consent') === 'all';
          // All four of Shopify's consent categories, set uniformly.
          // sale_of_data follows the same true = "may do this" polarity
          // as the others here (Shopify's own docs note it's "usually
          // false after the visitor opts out"), so "essential only" opts
          // out of sale/sharing along with analytics and marketing.
          var consent = {
            analytics: granted,
            marketing: granted,
            preferences: granted,
            sale_of_data: granted,
          };

          var cp = window.Shopify && window.Shopify.customerPrivacy;
          if (cp && typeof cp.setTrackingConsent === 'function') {
            try {
              cp.setTrackingConsent(consent, submitForm);
              return;
            } catch (e) {
              // fall through to submitting anyway — never let a consent
              // API failure trap the visitor on an unresponsive popup.
            }
          }
          submitForm();
        });
      });
    }

    if (manageLink) {
      manageLink.addEventListener('click', function (event) {
        event.preventDefault();
        // Deliberately does NOT hide our own modal, and does NOT call
        // remember(): the visitor hasn't made a location choice yet, so
        // the popup needs to still be there — and still open — once
        // Shopify's preferences UI closes, whether they save, cancel, or
        // click its own X. There's no reliable cross-browser event for
        // "the preferences UI just closed" to hook instead, so the fix is
        // to never hide ours in the first place: Shopify's preferences
        // dialog renders at z-index ~2,000,000 (confirmed live), our
        // modal sits at 100, so theirs fully covers ours while open and
        // ours is simply still there, already open, the moment theirs
        // goes away — nothing to re-show.
        //
        // An earlier version called modal.hide() here on the theory that
        // two overlays should never stack. In practice that meant once a
        // visitor closed Shopify's preferences UI, our popup had vanished
        // with no way back to it short of reloading the page — worse than
        // the harmless super-brief moment of both existing in the DOM.
        if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
          window.privacyBanner.showPreferences();
        }
      });
    }
  }

  function bind(modal) {
    if (!modal || modal.dataset.locationPopupBound || typeof modal.show !== 'function') return;
    modal.dataset.locationPopupBound = 'true';

    var rememberDays = parseInt(modal.dataset.remember, 10) || 30;
    var delay = parseInt(modal.dataset.delay, 10) || 0;

    function remember() {
      setCookie(COOKIE_NAME, '1', rememberDays);
    }

    // "Continue browsing" (and the X close button) just close the popup.
    modal.querySelectorAll('[data-location-popup-dismiss]').forEach(function (button) {
      button.addEventListener('click', function () {
        remember();
        modal.hide();
      });
    });

    // Submitting the country/language form navigates away — remember
    // before that navigation starts. Fires for every real form submission
    // (native click, or requestSubmit() from the consent buttons above);
    // it does NOT fire for the manage-cookies link, which never submits.
    var form = modal.querySelector('.location-popup-modal__form');
    if (form) {
      form.addEventListener('submit', remember);
      setupLocationAndConsentActions(modal, form);
      setupCountryCombobox(modal, form);
    }

    // In the theme editor, only show while this section is the one
    // selected in the sidebar — see the file-level comment above.
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      if (editorSelected) modal.show();
      return;
    }

    // Sliding expiry. Anyone who has already answered gets their cookie
    // pushed out another `rememberDays` on every visit, so a regular
    // customer is never re-prompted. Re-asking someone who already told us
    // is pure friction with nothing gained, and if their situation does
    // change the country selector is in the header and footer on every
    // page. To deliberately force everyone to answer again — new markets,
    // reworded question — bump COOKIE_NAME to a new value rather than
    // shortening the window, so the re-ask happens when you decide it
    // should instead of on a timer.
    if (getCookie(COOKIE_NAME)) {
      remember();
      return;
    }

    // Already resolved via a cross-domain market redirect — remember
    // silently on this domain too instead of showing the popup again.
    if (arrivedViaMarketRedirect()) {
      remember();
      return;
    }

    // Deliberately after both early returns above: returning visitors and
    // visitors arriving mid-redirect never trigger the request at all.
    // The show_delay (750ms by default) normally covers the round trip, so
    // the dropdown is already corrected before the popup becomes visible;
    // if it isn't, the popup opens on the served country and the value is
    // patched in a moment later.
    prefillDetectedCountry(modal);

    window.setTimeout(function () {
      modal.show();
    }, delay);
  }

  function refresh() {
    var modals = document.querySelectorAll(SELECTOR);
    if (!modals.length) return;
    var latest = modals[modals.length - 1];
    removeStaleModals(latest);
    bind(latest);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  // Re-sync whenever the theme editor live-reloads a section (e.g. after
  // a settings change).
  document.addEventListener('shopify:section:load', refresh);

  // Show/hide as the merchant selects or deselects this section in the
  // theme editor sidebar, so it's only in the way when they're actually
  // working on it.
  document.addEventListener('shopify:section:select', function (event) {
    var modal = document.querySelector(SELECTOR);
    if (!modal || event.detail.sectionId !== modal.dataset.sectionId) return;
    editorSelected = true;
    modal.show();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var modal = document.querySelector(SELECTOR);
    if (!modal || event.detail.sectionId !== modal.dataset.sectionId) return;
    editorSelected = false;
    modal.hide();
  });
})();
